// Máscaras para formatação de campos

/** Remove não-dígitos e completa CPF com zeros à esquerda até 11 dígitos */
export const normalizeCpf = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.length <= 11 ? digits.padStart(11, '0') : digits;
};

/** Remove não-dígitos e completa CNPJ com zeros à esquerda até 14 dígitos */
export const normalizeCnpj = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.length <= 14 ? digits.padStart(14, '0') : digits;
};

export const maskCPF = (value) => {
  if (!value) return '';
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
};

export const maskCNPJ = (value) => {
  if (!value) return '';
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
};

export const maskPhone = (value) => {
  if (!value) return '';
  value = value.replace(/\D/g, '');
  
  if (value.length <= 10) {
    return value
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  } else {
    return value
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  }
};

export const maskCEP = (value) => {
  if (!value) return '';
  return value
    .replace(/\D/g, '')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{3})\d+?$/, '$1');
};

export const maskMoney = (value) => {
  if (!value) return '';
  
  // Remove tudo que não é dígito
  let numericValue = value.replace(/\D/g, '');
  
  // Converte para número e divide por 100
  const floatValue = parseFloat(numericValue) / 100;
  
  // Formata como moeda brasileira
  return floatValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const unmaskMoney = (value) => {
  if (!value) return '0.00';
  
  // Remove tudo que não é dígito
  const numericValue = value.replace(/\D/g, '');
  
  // Converte para decimal
  return (parseFloat(numericValue) / 100).toFixed(2);
};

// Validações
export const validateCPF = (cpf) => {
  cpf = cpf.replace(/\D/g, '');
  
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
    return false;
  }
  
  let sum = 0;
  let remainder;
  
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
  }
  
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(9, 10))) return false;
  
  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
  }
  
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(10, 11))) return false;
  
  return true;
};

export const validateCNPJ = (cnpj) => {
  cnpj = cnpj.replace(/\D/g, '');
  
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) {
    return false;
  }
  
  let length = cnpj.length - 2;
  let numbers = cnpj.substring(0, length);
  const digits = cnpj.substring(length);
  let sum = 0;
  let pos = length - 7;
  
  for (let i = length; i >= 1; i--) {
    sum += numbers.charAt(length - i) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;
  
  length = length + 1;
  numbers = cnpj.substring(0, length);
  sum = 0;
  pos = length - 7;
  
  for (let i = length; i >= 1; i--) {
    sum += numbers.charAt(length - i) * pos--;
    if (pos < 2) pos = 9;
  }
  
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;
  
  return true;
};

// Função auxiliar para obter componentes de data no fuso horário de Brasília
const getBrasiliaDateComponents = (date) => {
  if (!date) return null;
  const d = new Date(date);
  
  // Usar Intl.DateTimeFormat para obter componentes no fuso horário de Brasília
  const formatter = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(d);
  const getPart = (type) => parts.find(p => p.type === type)?.value || '00';
  
  return {
    day: getPart('day'),
    month: getPart('month'),
    year: getPart('year'),
    hour: getPart('hour'),
    minute: getPart('minute')
  };
};

export const formatDate = (date) => {
  if (!date) return '';
  const components = getBrasiliaDateComponents(date);
  if (!components) return '';
  return `${components.day}/${components.month}/${components.year}`;
};

export const formatDateTime = (date) => {
  if (!date) return '';
  const components = getBrasiliaDateComponents(date);
  if (!components) return '';
  return `${components.day}/${components.month}/${components.year}, ${components.hour}:${components.minute}`;
};

/**
 * Formata datas "apenas data" (ex: data_nascimento, data_agendamento) sem aplicar fuso.
 * Evita que "2025-01-29" vire dia 28 no modal (quando backend envia 00:00 UTC).
 */
export const formatDateOnly = (date) => {
  if (!date) return '';
  const str = String(date);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return formatDate(date);
};

/**
 * Formata datas de pagamento/conclusão (campos que são "data calendário").
 * Sempre extrai YYYY-MM-DD do início da string e exibe como dd/mm/yyyy.
 * Ignora timezone pois datas de calendário não devem ser afetadas por UTC.
 * Ex: "2026-03-10" ou "2026-03-10T00:00:00.000Z" → "10/03/2026"
 */
export const formatDateForDisplay = (date) => {
  if (!date) return '';
  const str = String(date).trim();
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

/**
 * Extrai "YYYY-MM-DD" de string (ISO ou data pura) para usar em input type="date".
 * Ex: "2026-03-10T00:00:00.000Z" ou "2026-03-10" → "2026-03-10"
 */
export const toDateInputValue = (date) => {
  if (!date) return '';
  const m = String(date).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? m[0] : '';
};

/** Retorna hoje em Brasília no formato YYYY-MM-DD para input type="date". */
export const getTodayBrasilia = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });

/**
 * Formata "YYYY-MM-DD" -> "dd/mm/yyyy" para exibir em inputs de filtro.
 * Evita bug de timezone no input type="date" no mobile (1 dia a menos).
 */
export const formatDateToDDMMYYYY = (iso) => {
  if (!iso || typeof iso !== 'string') return '';
  const m = iso.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

/**
 * Parse "dd/mm/yyyy" ou "dd/mm/yy" -> "YYYY-MM-DD". Retorna null se inválido.
 * Usado em filtros para evitar bug do calendário nativo no celular.
 */
export const parseDateDDMMYYYY = (str) => {
  if (!str || typeof str !== 'string') return null;
  const s = str.replace(/\D/g, '');
  if (s.length !== 8) return null;
  const d = s.slice(0, 2), M = s.slice(2, 4), y = s.slice(4, 8);
  const day = parseInt(d, 10), month = parseInt(M, 10), year = parseInt(y, 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day > last) return null;
  return `${year}-${M}-${d}`;
};

/** Formata digitação em dd/mm/yyyy (só dígitos, insere barras). */
export const maskDateDDMMYYYY = (raw) => {
  const d = String(raw || '').replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

export const formatPhone = (phone) => {
  if (!phone) return '';
  const clean = phone.replace(/\D/g, '');
  if (clean.length === 11) {
    return clean.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (clean.length === 10) {
    return clean.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return phone;
};

export const formatCPF = (cpf) => {
  if (!cpf) return '';
  const clean = cpf.replace(/\D/g, '');
  if (clean.length === 11) {
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return cpf;
};

export const formatCNPJ = (cnpj) => {
  if (!cnpj) return '';
  const clean = cnpj.replace(/\D/g, '');
  if (clean.length === 14) {
    return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return cnpj;
};

export const formatDocumento = (doc) => {
  if (!doc) return '';
  
  // Converte para string caso venha como número
  const docStr = String(doc);
  
  // Remove pontos e outros caracteres não numéricos
  const clean = docStr.replace(/\D/g, '');
  
  if (clean.length === 11) {
    return formatCPF(docStr);
  } else if (clean.length === 14) {
    return formatCNPJ(docStr);
  }
  return docStr;
};

/** Formata código PAP no padrão XX-###### ou XX-####### (ex: SC-339683 ou SC-3396832) */
export const maskCodigoPap = (value) => {
  if (!value) return '';
  const upper = value.toUpperCase();
  const letters = upper.replace(/[^A-Z]/g, '').slice(0, 2);
  let digits = '';
  const hyphenIdx = upper.indexOf('-');
  if (hyphenIdx !== -1) {
    digits = upper.slice(hyphenIdx + 1).replace(/\D/g, '').slice(0, 7);
  } else if (letters.length === 2) {
    digits = upper.slice(2).replace(/\D/g, '').slice(0, 7);
  }
  if (letters.length < 2) return letters;
  if (!digits) return `${letters}-`;
  return `${letters}-${digits}`;
};

/** Valida código PAP: 2 letras + hífen + 6 ou 7 números */
export const validateCodigoPap = (value) => {
  if (!value) return false;
  return /^[A-Z]{2}-\d{6,7}$/.test(value.trim().toUpperCase());
};

/**
 * Normaliza número da ordem ONDA para 10 dígitos + 1 letra (ex: 7843221527A).
 * Aceita colagens como "ordem (7843221527A)", espaços e um dígito a mais.
 */
export const normalizeNumeroOrdemOnda = (raw) => {
  if (!raw) return '';
  const upper = String(raw).toUpperCase().replace(/\s+/g, '');

  const match = upper.match(/(\d{10,})([A-Z])/);
  if (match) {
    return match[1].slice(0, 10) + match[2];
  }

  let digits = '';
  let letter = '';
  for (const ch of upper) {
    if (/\d/.test(ch) && !letter) {
      if (digits.length < 10) digits += ch;
    } else if (/[A-Z]/.test(ch) && digits.length > 0 && !letter) {
      letter = ch;
    }
  }
  return digits + letter;
};

/** Valida ordem ONDA: exatamente 10 números + 1 letra (ex: 7843221527A) */
export const validateNumeroOrdemOnda = (value) => {
  return /^\d{10}[A-Z]$/.test(String(value || '').trim().toUpperCase());
};
